import { RT4K_MODE_PAD, rt4kArrayElementOffset, rt4kFieldOffset, type Rt4kStructFieldName } from './layout';
import type { ByteRange } from './schema';

export interface InputModeDefinition {
  slot: number;
  label: string;
  group: 'TVP7002' | 'SDP' | 'HDMI' | 'Unknown';
  implemented: boolean;
}

export interface InputModeResolution {
  width: number;
  height: number;
}

export type InputModeAspectCorrection = '4:3 (PAR)' | '1:1 (N/A)';

export interface InputModeAspectMetadata {
  defaultAspectCorrection: InputModeAspectCorrection;
  locked: boolean;
  hdmiFourThreeDisplayRatio?: number;
}

const VARIABLE_FOUR_THREE_INPUT_SLOTS = new Set([
  0, 1, 2, 3, 4, 5, 6, 10, 11, 12, 13, 17, 96, 97, 98, 99,
]);

const HDMI_NTSC_PAR_DISPLAY_RATIO = 15 / 11;
const HDMI_PAL_PAR_DISPLAY_RATIO = 295 / 216;
const HDMI_FOUR_THREE_DISPLAY_RATIO = 4 / 3;

const HDMI_PAR_DISPLAY_RATIOS = new Map<number, number>([
  [101, HDMI_NTSC_PAR_DISPLAY_RATIO],
  [102, HDMI_NTSC_PAR_DISPLAY_RATIO],
  [103, HDMI_NTSC_PAR_DISPLAY_RATIO],
  [104, HDMI_PAL_PAR_DISPLAY_RATIO],
  [105, HDMI_PAL_PAR_DISPLAY_RATIO],
  [106, HDMI_NTSC_PAR_DISPLAY_RATIO],
  [107, HDMI_NTSC_PAR_DISPLAY_RATIO],
  [108, HDMI_PAL_PAR_DISPLAY_RATIO],
  [109, HDMI_PAL_PAR_DISPLAY_RATIO],
  [114, HDMI_FOUR_THREE_DISPLAY_RATIO],
  [118, HDMI_FOUR_THREE_DISPLAY_RATIO],
  [119, HDMI_NTSC_PAR_DISPLAY_RATIO],
  [120, HDMI_NTSC_PAR_DISPLAY_RATIO],
  [121, HDMI_NTSC_PAR_DISPLAY_RATIO],
  [122, HDMI_PAL_PAR_DISPLAY_RATIO],
  [123, HDMI_PAL_PAR_DISPLAY_RATIO],
  [124, HDMI_FOUR_THREE_DISPLAY_RATIO],
]);

const LOCKED_SQUARE_PIXEL_INPUT_SLOTS = new Set([
  7, 8, 9, 14, 15, 16, 18, 19,
  ...Array.from({ length: 25 }, (_, index) => index + 69),
  100, 110, 111, 112, 113, 115, 116, 117,
]);

const INPUT_MODE_SETTING_FIELDS = new Map<string, Rt4kStructFieldName>([
  ['advanced.scaling.input_crop.right_trim', 'scaler_trim_right'],
  ['advanced.scaling.input_crop.left_trim', 'scaler_trim_left'],
  ['advanced.scaling.input_crop.top_trim', 'scaler_trim_top'],
  ['advanced.scaling.input_crop.bottom_trim', 'scaler_trim_bottom'],
  ['advanced.scaling.input_crop.vertical_prescale', 'scaler_pre_scale'],
  ['advanced.scaling.scaler.aspect_correction', 'scaler_ar_mode'],
  ['advanced.scaling.scaler.scaling_mode', 'scaler_mode'],
  ['advanced.scaling.scaler.vertical_pan', 'scaler_vert_pan'],
  ['advanced.effects.interpolation.horizontal_kernel', 'scaler_hori_kernel'],
  ['advanced.effects.interpolation.vertical_kernel', 'scaler_vert_kernel'],
  ['advanced.acquisition.hdmi_receiver.input_decimation.input_pixels', 'tvp_dec_factor'],
  ['advanced.acquisition.hdmi_receiver.input_decimation.initial_phase', 'tvp_dec_phase'],
  ['advanced.acquisition.hdmi_receiver.input_decimation.output_pixels', 'hdmi_dec_num'],
  ['advanced.acquisition.rgb_adc.sampling.samples_per_line', 'tvp_sr_override'],
  ['advanced.acquisition.rgb_adc.sampling.decimation_factor', 'tvp_dec_factor'],
  ['advanced.acquisition.rgb_adc.sampling.decimation_phase', 'tvp_dec_phase'],
  ['advanced.acquisition.rgb_adc.sampling.sub_phase', 'tvp_phase_override'],
  ['advanced.acquisition.rgb_adc.sampling.anti_alias_lpf', 'tvp_aa_filter'],
  ['advanced.acquisition.rgb_adc.sync.sog_threshold', 'tvp_sog_override'],
  ['advanced.acquisition.rgb_adc.sync.pre_coast', 'tvp_pre_coast_override'],
  ['advanced.acquisition.rgb_adc.sync.post_coast', 'tvp_post_coast_override'],
  ['advanced.acquisition.rgb_adc.gain.pre_adc', 'tvp_pre_gain_override'],
  ['advanced.acquisition.rgb_adc.gain.red', 'tvp_gain_r_override'],
  ['advanced.acquisition.rgb_adc.gain.green', 'tvp_gain_g_override'],
  ['advanced.acquisition.rgb_adc.gain.blue', 'tvp_gain_b_override'],
  ['advanced.acquisition.rgb_adc.offset.red', 'tvp_off_r_override'],
  ['advanced.acquisition.rgb_adc.offset.green', 'tvp_off_g_override'],
  ['advanced.acquisition.rgb_adc.offset.blue', 'tvp_off_b_override'],
]);

const SLOT_DATA_FIELDS: Rt4kStructFieldName[] = [
  'scaler_trim_right',
  'scaler_trim_left',
  'scaler_trim_top',
  'scaler_trim_bottom',
  'scaler_pre_scale',
  'scaler_ar_mode',
  'scaler_mode',
  'scaler_vert_pan',
  'scaler_vert_ar_factor',
  'scaler_hori_free_factor',
  'scaler_vert_free_factor',
  'scaler_hori_kernel',
  'scaler_vert_kernel',
  'tvp_sr_override',
  'tvp_dec_factor',
  'tvp_dec_phase',
  'tvp_phase_override',
  'tvp_aa_filter',
  'tvp_sog_override',
  'tvp_pre_coast_override',
  'tvp_post_coast_override',
  'tvp_pre_gain_override',
  'tvp_gain_r_override',
  'tvp_gain_g_override',
  'tvp_gain_b_override',
  'hdmi_dec_num',
  'tvp_off_r_override',
  'tvp_off_g_override',
  'tvp_off_b_override',
];

const TVP_OFFSET_FIELDS = new Set<Rt4kStructFieldName>([
  'tvp_off_r_override',
  'tvp_off_g_override',
  'tvp_off_b_override',
]);

const TVP_MODE_DEFINITIONS: InputModeDefinition[] = [
  { slot: 0, label: 'No Signal', group: 'TVP7002', implemented: true },
  { slot: 1, label: 'CP 480i', group: 'TVP7002', implemented: true },
  { slot: 2, label: 'CP 240p', group: 'TVP7002', implemented: true },
  { slot: 3, label: 'CP 576i', group: 'TVP7002', implemented: true },
  { slot: 4, label: 'CP 288p', group: 'TVP7002', implemented: true },
  { slot: 5, label: 'CP 480p', group: 'TVP7002', implemented: true },
  { slot: 6, label: 'CP 576p', group: 'TVP7002', implemented: true },
  { slot: 7, label: 'CP 720p', group: 'TVP7002', implemented: true },
  { slot: 8, label: 'CP 1080i', group: 'TVP7002', implemented: true },
  { slot: 9, label: 'CP 1080p', group: 'TVP7002', implemented: true },
  { slot: 10, label: 'DOS 400p70', group: 'TVP7002', implemented: true },
  { slot: 11, label: 'DOS 350p70', group: 'TVP7002', implemented: true },
  { slot: 12, label: 'SVGA 800x600', group: 'TVP7002', implemented: true },
  { slot: 13, label: 'XGA 1024x768', group: 'TVP7002', implemented: true },
  { slot: 14, label: 'VESA 1280x960', group: 'TVP7002', implemented: true },
  { slot: 15, label: 'VESA 1280x1024', group: 'TVP7002', implemented: true },
  { slot: 16, label: 'VESA 1600x1200', group: 'TVP7002', implemented: true },
  { slot: 17, label: 'VGA 640x480', group: 'TVP7002', implemented: true },
  { slot: 18, label: 'Unknown', group: 'TVP7002', implemented: true },
  { slot: 19, label: 'PC 1368x768', group: 'TVP7002', implemented: true },
  ...Array.from({ length: 49 }, (_, index) => ({
    slot: index + 20,
    label: `Future ${index + 2}`,
    group: 'TVP7002' as const,
    implemented: false,
  })),
  { slot: 69, label: 'Custom Input Mode No Slot', group: 'TVP7002', implemented: true },
  ...Array.from({ length: 24 }, (_, index) => ({
    slot: index + 70,
    label: `Custom Input Mode ${index + 1}`,
    group: 'TVP7002' as const,
    implemented: true,
  })),
];

const OTHER_MODE_DEFINITIONS: InputModeDefinition[] = [
  { slot: 96, label: 'SDP 240p', group: 'SDP', implemented: true },
  { slot: 97, label: 'SDP 480i', group: 'SDP', implemented: true },
  { slot: 98, label: 'SDP 288p', group: 'SDP', implemented: true },
  { slot: 99, label: 'SDP 576i', group: 'SDP', implemented: true },
  { slot: 100, label: 'HDMI® Custom', group: 'HDMI', implemented: true },
  { slot: 101, label: 'HDMI® 480i', group: 'HDMI', implemented: true },
  { slot: 102, label: 'HDMI® 240p', group: 'HDMI', implemented: true },
  { slot: 103, label: 'HDMI® 480p', group: 'HDMI', implemented: true },
  { slot: 104, label: 'HDMI® 576i', group: 'HDMI', implemented: true },
  { slot: 105, label: 'HDMI® 288p', group: 'HDMI', implemented: true },
  { slot: 106, label: 'HDMI® 480i (SR)', group: 'HDMI', implemented: true },
  { slot: 107, label: 'HDMI® 240p (SR)', group: 'HDMI', implemented: true },
  { slot: 108, label: 'HDMI® 576i (SR)', group: 'HDMI', implemented: true },
  { slot: 109, label: 'HDMI® 288p (SR)', group: 'HDMI', implemented: true },
  { slot: 110, label: 'HDMI® 576p', group: 'HDMI', implemented: true },
  { slot: 111, label: 'HDMI® 720p', group: 'HDMI', implemented: true },
  { slot: 112, label: 'HDMI® 1080i', group: 'HDMI', implemented: true },
  { slot: 113, label: 'HDMI® 1080p', group: 'HDMI', implemented: true },
  { slot: 114, label: 'HDMI® 640x480', group: 'HDMI', implemented: true },
  { slot: 115, label: 'HDMI® 800x600', group: 'HDMI', implemented: true },
  { slot: 116, label: 'HDMI® 1024x768', group: 'HDMI', implemented: true },
  { slot: 117, label: 'HDMI® 1280x1024', group: 'HDMI', implemented: true },
  { slot: 118, label: 'HDMI® GBI', group: 'HDMI', implemented: true },
  { slot: 119, label: 'HDMI® 960i', group: 'HDMI', implemented: true },
  { slot: 120, label: 'MiSTer 240p', group: 'HDMI', implemented: true },
  { slot: 121, label: 'MiSTer 480i', group: 'HDMI', implemented: true },
  { slot: 122, label: 'MiSTer 288p', group: 'HDMI', implemented: true },
  { slot: 123, label: 'MiSTer 576i', group: 'HDMI', implemented: true },
  { slot: 124, label: 'MiSTer Gen.', group: 'HDMI', implemented: true },
];

export const INPUT_MODE_DEFINITIONS: InputModeDefinition[] = [...TVP_MODE_DEFINITIONS, ...OTHER_MODE_DEFINITIONS].sort(
  (left, right) => left.slot - right.slot,
);

const INPUT_MODE_DEFINITIONS_BY_SLOT = new Map(INPUT_MODE_DEFINITIONS.map((definition) => [definition.slot, definition]));

const INPUT_MODE_NOMINAL_RESOLUTIONS = new Map<number, InputModeResolution>([
  [1, { width: 1440, height: 480 }],
  [2, { width: 1440, height: 240 }],
  [3, { width: 1440, height: 576 }],
  [4, { width: 1440, height: 288 }],
  [5, { width: 1440, height: 480 }],
  [6, { width: 1440, height: 576 }],
  [7, { width: 1280, height: 720 }],
  [8, { width: 1920, height: 1080 }],
  [9, { width: 1920, height: 1080 }],
  [10, { width: 1638, height: 400 }],
  [11, { width: 1638, height: 350 }],
  [12, { width: 1600, height: 600 }],
  [13, { width: 2048, height: 768 }],
  [14, { width: 1280, height: 960 }],
  [15, { width: 1280, height: 1024 }],
  [16, { width: 1600, height: 1200 }],
  [17, { width: 1280, height: 480 }],
  [19, { width: 1368, height: 768 }],
  [96, { width: 720, height: 240 }],
  [97, { width: 720, height: 480 }],
  [98, { width: 720, height: 288 }],
  [99, { width: 720, height: 576 }],
  [101, { width: 720, height: 480 }],
  [102, { width: 720, height: 240 }],
  [103, { width: 720, height: 480 }],
  [104, { width: 720, height: 576 }],
  [105, { width: 720, height: 288 }],
  [110, { width: 720, height: 576 }],
  [111, { width: 1280, height: 720 }],
  [112, { width: 1920, height: 1080 }],
  [113, { width: 1920, height: 1080 }],
  [114, { width: 640, height: 480 }],
  [115, { width: 800, height: 600 }],
  [116, { width: 1024, height: 768 }],
  [117, { width: 1280, height: 1024 }],
  [118, { width: 960, height: 360 }],
  [119, { width: 720, height: 960 }],
]);

const INPUT_MODE_DEFAULT_SAMPLE_RATES = new Map<number, number>([
  [0, 1716],
  [1, 1716],
  [2, 1716],
  [3, 1728],
  [4, 1728],
  [5, 1716],
  [6, 1728],
  [7, 1650],
  [8, 2200],
  [9, 2200],
  [10, 2048],
  [11, 2048],
  [12, 2112],
  [13, 2688],
  [14, 1712],
  [15, 1688],
  [16, 2160],
  [17, 1600],
  [18, 2200],
  [19, 1800],
]);

export function inputModeDefinitionForSlot(slot: number): InputModeDefinition {
  return INPUT_MODE_DEFINITIONS_BY_SLOT.get(slot) ?? {
    slot,
    label: `Input Mode ${slot}`,
    group: 'Unknown',
    implemented: true,
  };
}

export function inputModeOptionLabel(slot: number): string {
  const definition = inputModeDefinitionForSlot(slot);
  return `${definition.slot}: ${definition.label}`;
}

export function inputModeNominalResolutionForSlot(slot: number | null | undefined): InputModeResolution | null {
  if (slot === null || slot === undefined) {
    return null;
  }

  return INPUT_MODE_NOMINAL_RESOLUTIONS.get(slot) ?? null;
}

export function inputModeNominalHeightForSlot(slot: number | null | undefined): number | null {
  return inputModeNominalResolutionForSlot(slot)?.height ?? null;
}

export function inputModeDefaultSampleRateForSlot(slot: number | null | undefined): number | null {
  if (slot === null || slot === undefined) {
    return null;
  }

  return INPUT_MODE_DEFAULT_SAMPLE_RATES.get(slot) ?? null;
}

export function inputModeSampleRateForSlot(bytes: Uint8Array, slot: number | null | undefined): number | null {
  const defaultSampleRate = inputModeDefaultSampleRateForSlot(slot);
  if (defaultSampleRate === null || slot === null || slot === undefined) {
    return null;
  }

  const address = rt4kArrayElementOffset('tvp_sr_override', slot);
  if (address + 2 > bytes.length) {
    return null;
  }

  const sampleRateOffset = new DataView(bytes.buffer, bytes.byteOffset + address, 2).getInt16(0, true);
  return defaultSampleRate + sampleRateOffset;
}

export function inputModeDefaultAspectCorrection(slot: number | null | undefined): string | null {
  return inputModeAspectMetadataForSlot(slot)?.defaultAspectCorrection ?? null;
}

export function inputModeAspectMetadataForSlot(
  slot: number | null | undefined,
): InputModeAspectMetadata | null {
  if (slot === null || slot === undefined) {
    return null;
  }

  const hdmiFourThreeDisplayRatio = HDMI_PAR_DISPLAY_RATIOS.get(slot);
  if (hdmiFourThreeDisplayRatio !== undefined) {
    return {
      defaultAspectCorrection: '4:3 (PAR)',
      locked: false,
      hdmiFourThreeDisplayRatio,
    };
  }

  if (LOCKED_SQUARE_PIXEL_INPUT_SLOTS.has(slot)) {
    return {
      defaultAspectCorrection: '1:1 (N/A)',
      locked: true,
    };
  }

  return VARIABLE_FOUR_THREE_INPUT_SLOTS.has(slot)
    ? { defaultAspectCorrection: '4:3 (PAR)', locked: false }
    : null;
}

export function inputModeByteRangesForSettingPath(path: string, slot: number | null | undefined): ByteRange[] | null {
  if (slot === null || slot === undefined) {
    return null;
  }

  const fieldName = INPUT_MODE_SETTING_FIELDS.get(path);
  if (!fieldName || slot < 0 || slot >= RT4K_MODE_PAD) {
    return null;
  }

  const address = inputModeFieldAddress(fieldName, slot);
  if (address === null) {
    return [];
  }

  return [
    {
      address,
      length: inputModeFieldElementLength(fieldName),
    },
  ];
}

export function isInputModeSlotSettingPath(path: string): boolean {
  return INPUT_MODE_SETTING_FIELDS.has(path);
}

export function populatedInputModeDefinitions(bytes: Uint8Array): InputModeDefinition[] {
  return INPUT_MODE_DEFINITIONS.filter((definition) => inputModeSlotHasData(bytes, definition.slot));
}

export function inputModeDataByteRanges(slot: number): ByteRange[] {
  return SLOT_DATA_FIELDS.flatMap((fieldName) => {
    const address = inputModeFieldAddress(fieldName, slot);
    return address === null ? [] : [{ address, length: inputModeFieldElementLength(fieldName) }];
  });
}

export function inputModeSlotHasData(bytes: Uint8Array, slot: number): boolean {
  if (slot < 0 || slot >= RT4K_MODE_PAD) {
    return false;
  }

  return inputModeSlotHasAnyFieldData(bytes, slot, SLOT_DATA_FIELDS);
}

function inputModeSlotHasAnyFieldData(bytes: Uint8Array, slot: number, fieldNames: Rt4kStructFieldName[]): boolean {
  if (slot < 0 || slot >= RT4K_MODE_PAD) {
    return false;
  }

  return fieldNames.some((fieldName) => inputModeFieldHasData(bytes, fieldName, slot));
}

function inputModeFieldHasData(bytes: Uint8Array, fieldName: Rt4kStructFieldName, slot: number): boolean {
  const address = inputModeFieldAddress(fieldName, slot);
  if (address === null) {
    return false;
  }

  const length = inputModeFieldElementLength(fieldName);
  if (address + length > bytes.length) {
    return false;
  }

  if (length === 2) {
    return new DataView(bytes.buffer, bytes.byteOffset + address, 2).getInt16(0, true) !== 0;
  }

  return (bytes[address] ?? 0) !== 0;
}

function inputModeFieldAddress(fieldName: Rt4kStructFieldName, slot: number): number | null {
  if (TVP_OFFSET_FIELDS.has(fieldName)) {
    if (slot < 1 || slot >= RT4K_MODE_PAD) {
      return null;
    }

    // Device-authored profiles place CP 480i one element before the corrected
    // CP 240p base represented by layout.ts. Continue that linear addressing
    // instead of aliasing slots 1 and 2.
    return rt4kFieldOffset(fieldName) + (slot - 2) * 2;
  }

  return slot >= 0 && slot < RT4K_MODE_PAD ? rt4kArrayElementOffset(fieldName, slot) : null;
}

function inputModeFieldElementLength(fieldName: Rt4kStructFieldName): 1 | 2 {
  switch (fieldName) {
    case 'tvp_sr_override':
    case 'tvp_pre_gain_override':
    case 'tvp_gain_r_override':
    case 'tvp_gain_g_override':
    case 'tvp_gain_b_override':
    case 'scaler_trim_right':
    case 'scaler_trim_left':
    case 'scaler_trim_top':
    case 'scaler_trim_bottom':
    case 'scaler_vert_ar_factor':
    case 'scaler_hori_free_factor':
    case 'scaler_vert_free_factor':
    case 'tvp_off_r_override':
    case 'tvp_off_g_override':
    case 'tvp_off_b_override':
      return 2;
    default:
      return 1;
  }
}
